import AdCard from "@/components/AdCard";
import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import PublicNav from "@/components/PublicNav";
import { Heart, MessageSquare, Trash2, Image as ImageIcon, Sparkles, Send } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SUGGEST_ROLES = ["employeur", "manager_employeur", "super_admin", "admin_lcs", "recruteur_lcs"];

function PostCard({ post, onChanged }) {
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null);
  const [suggesting, setSuggesting] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      const res = await api.get(`/posts/${post.post_id}/comments`);
      setComments(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [post.post_id]);

  useEffect(() => {
    if (showComments) loadComments();
  }, [showComments, loadComments]);

  const react = async () => {
    try {
      await api.post(`/posts/${post.post_id}/react`);
      onChanged();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setError("");
    try {
      await api.post(`/posts/${post.post_id}/comments`, { text: commentText });
      setCommentText("");
      await loadComments();
      onChanged();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/comments/${commentId}`);
      await loadComments();
      onChanged();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const deletePost = async () => {
    try {
      await api.delete(`/posts/${post.post_id}`);
      onChanged();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const suggestOffer = async () => {
    setSuggesting(true);
    setError("");
    setDraft(null);
    try {
      const res = await api.post(`/posts/${post.post_id}/suggest-offer`);
      setDraft(res.data.draft);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <article data-testid={`post-${post.post_id}`} className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-full bg-brand-sky flex items-center justify-center text-brand-navy font-bold shrink-0">
            {post.author.name?.[0]?.toUpperCase() || "?"}
          </span>
          <div className="min-w-0">
            <p className="font-semibold text-brand-navy text-sm truncate">
              {post.author.name}
              {post.author.nexa_id && (
                <Link
                  data-testid={`post-author-${post.post_id}`}
                  to={`/profil/${post.author.nexa_id}`}
                  className="ml-2 text-xs font-medium text-brand-turquoise hover:underline"
                >
                  @{post.author.nexa_id}
                </Link>
              )}
            </p>
            <p className="text-xs text-slate-400">
              {ROLE_LABELS[post.author.role] || post.author.role} · {post.created_at ? new Date(post.created_at).toLocaleString("fr-CA") : "—"}
            </p>
          </div>
        </div>
        {post.is_mine && (
          <button
            data-testid={`post-delete-${post.post_id}`}
            onClick={deletePost}
            title="Supprimer"
            className="text-slate-300 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <p data-testid={`post-text-${post.post_id}`} className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{post.text}</p>

      {post.image && (
        <img
          data-testid={`post-image-${post.post_id}`}
          src={`${API_BASE}/api/files/${post.image.file_id}/download`}
          alt={post.image.name}
          className="mt-3 rounded-lg border border-slate-100 max-h-80 w-auto"
        />
      )}

      {error && <p data-testid={`post-error-${post.post_id}`} className="text-xs text-red-600 mt-2">{error}</p>}

      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
        <button
          data-testid={`post-like-${post.post_id}`}
          onClick={react}
          className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${post.liked_by_me ? "text-brand-turquoise" : "text-slate-400 hover:text-brand-turquoise"}`}
        >
          <Heart className={`w-4 h-4 ${post.liked_by_me ? "fill-brand-turquoise" : ""}`} />
          {post.like_count}
        </button>
        <button
          data-testid={`post-comments-toggle-${post.post_id}`}
          onClick={() => setShowComments(!showComments)}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-brand-blue transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          {post.comment_count}
        </button>
        {SUGGEST_ROLES.includes(user?.role) && (
          <button
            data-testid={`post-suggest-offer-${post.post_id}`}
            onClick={suggestOffer}
            disabled={suggesting}
            className="flex items-center gap-1.5 text-xs font-medium text-brand-orange hover:text-brand-navy transition-colors disabled:opacity-50 ml-auto"
          >
            <Sparkles className="w-4 h-4" />
            {suggesting ? "Analyse IA…" : "Suggérer une offre (IA)"}
          </button>
        )}
      </div>

      {draft && (
        <div data-testid={`suggest-draft-${post.post_id}`} className="mt-3 bg-brand-sky/50 border border-brand-blue/20 rounded-lg p-4">
          <p className="text-xs font-semibold text-brand-blue uppercase tracking-wide mb-2">
            Brouillon IA (mode SUGGEST) — à valider humainement, rien n'a été créé
          </p>
          <p className="text-sm font-semibold text-brand-navy">{draft.title || "—"}</p>
          <p className="text-xs text-slate-600 mt-1 whitespace-pre-line">{draft.description}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(draft.skills || []).map((s) => (
              <span key={s} className="text-xs bg-white text-brand-navy rounded-full px-2.5 py-0.5 border border-slate-200">{s}</span>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {[draft.city, draft.country].filter(Boolean).join(", ")}
            {draft.noc_code ? ` · CNP/NOC ${draft.noc_code}` : ""}
            {draft.feer_level != null ? ` · FEER ${draft.feer_level}` : ""}
          </p>
          <Link
            data-testid={`suggest-goto-dashboard-${post.post_id}`}
            to="/dashboard"
            className="inline-block mt-3 text-xs font-semibold text-white bg-brand-navy rounded-lg px-4 py-2 hover:bg-brand-blue transition-colors"
          >
            Créer l'offre depuis mon tableau de bord
          </Link>
        </div>
      )}

      {showComments && (
        <div data-testid={`post-comments-${post.post_id}`} className="mt-3 pt-3 border-t border-slate-100 space-y-3">
          {comments.map((c) => (
            <div key={c.comment_id} data-testid={`comment-${c.comment_id}`} className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-brand-navy">{c.author_name}</p>
                <p className="text-sm text-slate-600 whitespace-pre-line">{c.text}</p>
              </div>
              {c.is_mine && (
                <button
                  data-testid={`comment-delete-${c.comment_id}`}
                  onClick={() => deleteComment(c.comment_id)}
                  className="text-slate-300 hover:text-red-500 transition-colors shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          {comments.length === 0 && <p className="text-xs text-slate-400">Aucun commentaire pour le moment.</p>}
          <form onSubmit={addComment} className="flex gap-2">
            <input
              data-testid={`comment-input-${post.post_id}`}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Écrire un commentaire…"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
            />
            <button data-testid={`comment-send-${post.post_id}`} type="submit" className="text-brand-turquoise hover:text-brand-navy transition-colors">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </article>
  );
}

export default function Network() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [feed, setFeed] = useState("all");
  const [text, setText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [error, setError] = useState("");
  const [publishing, setPublishing] = useState(false);
  const imageInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/posts", { params: { feed } });
      setPosts(res.data);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [feed]);

  useEffect(() => {
    load();
  }, [load]);

  const publish = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPublishing(true);
    setError("");
    try {
      let imageFileId = null;
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const up = await api.post("/posts/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        imageFileId = up.data.file_id;
      }
      await api.post("/posts", { text, image_file_id: imageFileId });
      setText("");
      setImageFile(null);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div data-testid="network-page" className="min-h-screen bg-slate-50">
      <PublicNav />
      <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        <AdCard placement="network" />
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-brand-navy">Réseau NEXA</h1>
          <div className="flex gap-2">
            {[
              { key: "all", label: "Tout" },
              { key: "following", label: "Abonnements" },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={`feed-tab-${t.key}`}
                onClick={() => setFeed(t.key)}
                className={`text-xs font-medium rounded-lg px-4 py-2 border transition-colors ${feed === t.key ? "bg-brand-navy text-white border-brand-navy" : "text-slate-600 border-slate-300 hover:bg-slate-50 bg-white"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <form data-testid="post-composer" onSubmit={publish} className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          {error && <div data-testid="network-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</div>}
          <textarea
            data-testid="post-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Quoi de neuf, ${user?.name?.split(" ")[0] || "membre"} ?`}
            rows={3}
            maxLength={2000}
            className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <input
                ref={imageInputRef}
                data-testid="post-image-input"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              />
              <button
                type="button"
                data-testid="post-image-button"
                onClick={() => imageInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50"
              >
                <ImageIcon className="w-3.5 h-3.5" />
                Image
              </button>
              {imageFile && (
                <span data-testid="post-image-selected" className="text-xs text-brand-navy bg-brand-sky rounded-full px-3 py-1">
                  {imageFile.name}
                </span>
              )}
            </div>
            <button
              data-testid="post-publish-button"
              type="submit"
              disabled={publishing || !text.trim()}
              className="bg-brand-turquoise text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-brand-navy transition-colors duration-200 disabled:opacity-50"
            >
              {publishing ? "Publication…" : "Publier"}
            </button>
          </div>
        </form>

        <div className="space-y-4">
          {posts.map((p) => (
            <PostCard key={p.post_id} post={p} onChanged={load} />
          ))}
          {posts.length === 0 && (
            <p data-testid="feed-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-10 text-center">
              {feed === "following"
                ? "Aucune publication de vos abonnements. Suivez des membres depuis leur profil public."
                : "Aucune publication pour le moment. Soyez le premier à publier !"}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
